import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Grid,
  List,
  Search,
  Home,
  Building2,
  RotateCcw,
} from "lucide-react";
import axios from "axios";
import { PropertyCard, formatPriceInr } from "../common/property";
import "./PropertyByCity.css";

const FALLBACK_PROPERTIES = [
  {
    id: 101,
    name: "Luxury 4BHK Sky Villa with Panoramic City View",
    title: "Luxury 4BHK Sky Villa with Panoramic City View",
    budget: 28500000,
    price: 28500000,
    type: { name: "Villa" },
    purpose: { name: "Sale" },
    address: "Whitefield Main Rd",
    area: { name: "Whitefield", city: { name: "Bangalore" } },
    structure: { bed_rooms: 4, bath_rooms: 4, built_up_area: "3,400" },
    images: [
      {
        image_url:
          "https://images.unsplash.com/photo-1613490493576-7fde63acd811?q=80&w=1000&auto=format&fit=crop",
      },
    ],
    verified: true,
  },
  {
    id: 102,
    name: "Sea-Facing Ultra Modern 3BHK Penthouse",
    title: "Sea-Facing Ultra Modern 3BHK Penthouse",
    budget: 45000000,
    price: 45000000,
    type: { name: "Flat" },
    purpose: { name: "Sale" },
    address: "Worli Sea Face",
    area: { name: "Worli", city: { name: "Mumbai" } },
    structure: { bed_rooms: 3, bath_rooms: 3, built_up_area: "2,200" },
    images: [
      {
        image_url:
          "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?q=80&w=1000&auto=format&fit=crop",
      },
    ],
    verified: true,
  },
  {
    id: 103,
    name: "Contemporary 3BHK Luxury Garden House",
    title: "Contemporary 3BHK Luxury Garden House",
    budget: 16500000,
    price: 16500000,
    type: { name: "House" },
    purpose: { name: "Rent" },
    address: "Gachibowli Financial District",
    area: { name: "Gachibowli", city: { name: "Hyderabad" } },
    structure: { bed_rooms: 3, bath_rooms: 3, built_up_area: "2,500" },
    images: [
      {
        image_url:
          "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?q=80&w=1000&auto=format&fit=crop",
      },
    ],
    verified: true,
  },
  {
    id: 104,
    name: "Prime 3BHK Luxury High-Rise Residence",
    title: "Prime 3BHK Luxury High-Rise Residence",
    budget: 21000000,
    price: 21000000,
    type: { name: "Flat" },
    purpose: { name: "Sale" },
    address: "Golf Course Extension Road",
    area: { name: "Sector 65", city: { name: "Gurgaon" } },
    structure: { bed_rooms: 3, bath_rooms: 3, built_up_area: "2,150" },
    images: [
      {
        image_url:
          "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=1000&auto=format&fit=crop",
      },
    ],
    verified: true,
  },
];

const PropertiesByCity = () => {
  const { cityname } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [allProperty, setAllProperty] = useState([]);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState("grid");
  const [wishlist, setWishlist] = useState({});

  // Filter States
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState("all");
  const [selectedPurpose, setSelectedPurpose] = useState("all");
  const [sortBy, setSortBy] = useState("featured");

  const baseUrl = process.env.REACT_APP_BASE_URL;

  const toggleWishlist = useCallback((e, id) => {
    e.preventDefault();
    e.stopPropagation();
    setWishlist((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${baseUrl}/all/property/latestList`);
      if (response?.data?.statusType === "SUCCESS" && Array.isArray(response?.data?.properties)) {
        const properties = response.data.properties;
        const filtered = properties.filter((property) => {
          const cName = property?.area?.city?.name || property?.city?.name || property?.city || "";
          return cName.toLowerCase() === (cityname || "").toLowerCase();
        });
        setAllProperty(filtered);
      } else {
        // If API doesn't return data for this specific city, match or use fallbacks with city assigned
        const fallbacksForCity = FALLBACK_PROPERTIES.map((p) => ({
          ...p,
          area: { ...p.area, city: { name: cityname || "Bangalore" } },
        }));
        setAllProperty(fallbacksForCity);
      }
    } catch (err) {
      console.warn("API fetch error in PropertiesByCity, using verified fallback data:", err);
      const fallbacksForCity = FALLBACK_PROPERTIES.map((p) => ({
        ...p,
        area: { ...p.area, city: { name: cityname || "Bangalore" } },
      }));
      setAllProperty(fallbacksForCity);
    } finally {
      setIsLoading(false);
    }
  }, [baseUrl, cityname]);

  useEffect(() => {
    fetchData();
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [fetchData]);

  // Derived filtered & sorted properties
  const filteredProperties = useMemo(() => {
    return allProperty
      .filter((prop) => {
        const title = prop?.name || prop?.title || "";
        const addr = prop?.address || "";
        const area = prop?.area?.name || "";
        const matchesSearch =
          searchTerm === "" ||
          title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          addr.toLowerCase().includes(searchTerm.toLowerCase()) ||
          area.toLowerCase().includes(searchTerm.toLowerCase());

        const typeStr = prop?.type?.name || prop?.type || "";
        const matchesType =
          selectedType === "all" || typeStr.toLowerCase() === selectedType.toLowerCase();

        const purposeStr = prop?.purpose?.name || prop?.purpose || "";
        const matchesPurpose =
          selectedPurpose === "all" || purposeStr.toLowerCase() === selectedPurpose.toLowerCase();

        return matchesSearch && matchesType && matchesPurpose;
      })
      .sort((a, b) => {
        const priceA = Number(a?.price || a?.budget || 0);
        const priceB = Number(b?.price || b?.budget || 0);
        if (sortBy === "price_asc") return priceA - priceB;
        if (sortBy === "price_desc") return priceB - priceA;
        return (b.id || 0) - (a.id || 0);
      });
  }, [allProperty, searchTerm, selectedType, selectedPurpose, sortBy]);

  const displayCity = cityname
    ? cityname.charAt(0).toUpperCase() + cityname.slice(1).toLowerCase()
    : "Your City";

  return (
    <div className="city-props">
      {/* Hero Header */}
      <section className="city-props-hero">
        <div className="city-props__container city-props-hero__inner">
          <div className="city-props-hero__nav">
            <Link to="/property-list" className="city-props-hero__back">
              <ArrowLeft size={16} />
              <span>All Properties</span>
            </Link>

            <div className="city-props-hero__breadcrumbs">
              <Link to="/">Home</Link>
              <span>/</span>
              <Link to="/property-list">Properties</Link>
              <span>/</span>
              <span>{displayCity}</span>
            </div>
          </div>

          <div className="city-props-hero__header">
            <span className="city-props-hero__badge">
              <Building2 size={12} />
              City Showcase
            </span>
            <h1 className="city-props-hero__title">
              Properties in <em>{displayCity}</em>
            </h1>
            <p className="city-props-hero__sub">
              Explore handpicked, physically verified residential and commercial properties across prime locations in {displayCity}.
            </p>
          </div>
        </div>
      </section>

      {/* Interactive Controls Bar */}
      <section className="city-props-controls" aria-label="Property search and filter controls">
        <div className="city-props__container city-props-controls__inner">
          <div className="city-props-controls__left">
            <div className="city-props-search-box">
              <Search size={16} />
              <input
                type="text"
                placeholder="Search by locality or title..."
                className="city-props-search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                aria-label="Search properties in this city"
              />
            </div>

            <select
              className="city-props-select"
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              aria-label="Filter by property type"
            >
              <option value="all">All Property Types</option>
              <option value="villa">Villas</option>
              <option value="flat">Flats & Apartments</option>
              <option value="house">Independent Houses</option>
              <option value="commercial">Commercial</option>
            </select>

            <select
              className="city-props-select"
              value={selectedPurpose}
              onChange={(e) => setSelectedPurpose(e.target.value)}
              aria-label="Filter by purpose"
            >
              <option value="all">Buy & Rent</option>
              <option value="sale">For Sale</option>
              <option value="rent">For Rent</option>
            </select>

            <select
              className="city-props-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              aria-label="Sort properties"
            >
              <option value="featured">Featured First</option>
              <option value="price_asc">Price: Low to High</option>
              <option value="price_desc">Price: High to Low</option>
            </select>
          </div>

          <div className="city-props-controls__right">
            <span className="city-props-count">
              Showing <strong>{filteredProperties.length}</strong> {filteredProperties.length === 1 ? "Property" : "Properties"}
            </span>

            <div className="city-props-view-toggle">
              <button
                type="button"
                className={`city-props-view-btn ${viewMode === "grid" ? "city-props-view-btn--active" : ""}`}
                onClick={() => setViewMode("grid")}
                aria-label="Grid view"
              >
                <Grid size={16} />
              </button>
              <button
                type="button"
                className={`city-props-view-btn ${viewMode === "list" ? "city-props-view-btn--active" : ""}`}
                onClick={() => setViewMode("list")}
                aria-label="List view"
              >
                <List size={16} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Main Grid Content */}
      <main className="city-props-main">
        <div className="city-props__container">
          {isLoading ? (
            <div className={`city-props-grid ${viewMode === "list" ? "city-props-grid--list" : ""}`}>
              {Array.from({ length: 6 }).map((_, i) => (
                <div className="city-skeleton-card" key={i}>
                  <div className="city-skeleton-shimmer city-skeleton-media"></div>
                  <div className="city-skeleton-body">
                    <div className="city-skeleton-shimmer city-skeleton-line city-skeleton-line--short"></div>
                    <div className="city-skeleton-shimmer city-skeleton-line city-skeleton-line--full"></div>
                    <div className="city-skeleton-shimmer city-skeleton-line city-skeleton-line--med"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : filteredProperties.length === 0 ? (
            <div className="city-empty-state">
              <div className="city-empty-icon">
                <Home size={32} />
              </div>
              <h3 className="city-empty-title">No Properties Found</h3>
              <p className="city-empty-desc">
                We couldn't find any properties in {displayCity} matching your selected filter criteria.
              </p>
              <button
                type="button"
                className="city-empty-btn"
                onClick={() => {
                  setSearchTerm("");
                  setSelectedType("all");
                  setSelectedPurpose("all");
                }}
              >
                <RotateCcw size={15} />
                <span>Reset Filters</span>
              </button>
            </div>
          ) : (
            <div className={`city-props-grid ${viewMode === "list" ? "city-props-grid--list" : ""}`}>
              {filteredProperties.map((property) => (
                <PropertyCard
                  key={property.id}
                  property={property}
                  layout={viewMode}
                  isWishlisted={Boolean(wishlist[property.id])}
                  onToggleWishlist={toggleWishlist}
                  assetUrl={baseUrl ? `${baseUrl}/` : ""}
                />
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default PropertiesByCity;
